import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useBranding } from '../contexts/BrandingContext';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import api from '../utils/api';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Building2,
  Briefcase,
  TrendingUp,
  UserCircle,
  FileText,
  BarChart3,
  X,
  Bot,
  Moon,
  Sun,
  Award,
  HelpCircle,
  Video,
  Megaphone,
  ClipboardCheck,
  FolderOpen,
  Edit,
  Euro,
  Ticket,
  Palette,
  Bug
} from 'lucide-react';
import { Button } from './ui/button';

const Sidebar = ({ isMobileOpen: isMobileOpenProp, setIsMobileOpen: setIsMobileOpenProp }) => {
  const { user, logout, theme, toggleTheme } = useAuth();
  const { chatUnreadCount, updateChatUnreadCount, lastMessage } = useWebSocket();
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const mobileMenu = useMobileMenu();

  // Prefer context (AppLayout); fall back to legacy props if provided
  const isMobileOpen =
    isMobileOpenProp !== undefined ? isMobileOpenProp : mobileMenu.isMobileOpen;
  const setIsMobileOpen = setIsMobileOpenProp || mobileMenu.setIsMobileOpen;
  
  // Badge counts for all 3 modules
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  // Fetch all unread counts
  const fetchAllUnreadCounts = useCallback(async () => {
    if (!user) return;
    
    try {
      // Fetch in parallel
      const [messagesRes, notificationsRes, supportRes] = await Promise.all([
        api.get('/messages/unread-count').catch(() => ({ data: { unread_count: 0 } })),
        api.get('/notifications/unread-count').catch(() => ({ data: { count: 0 } })),
        api.get('/support/unread-count').catch(() => ({ data: { count: 0 } }))
      ]);
      
      setMessageUnreadCount(messagesRes.data.unread_count || messagesRes.data.count || 0);
      setNotificationUnreadCount(notificationsRes.data.count || 0);
      setSupportUnreadCount(supportRes.data.count || 0);
      
      // Update WebSocket context for chat
      if (updateChatUnreadCount) {
        updateChatUnreadCount(messagesRes.data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread counts:', error);
    }
  }, [user, updateChatUnreadCount]);

  // Initial fetch and refetch on location change
  useEffect(() => {
    fetchAllUnreadCounts();
  }, [fetchAllUnreadCounts, location.pathname]);

  // Periodic refresh of badges (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAllUnreadCounts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchAllUnreadCounts]);

  // Listen for custom badge refresh events
  useEffect(() => {
    const handleBadgeRefresh = () => {
      fetchAllUnreadCounts();
    };
    
    window.addEventListener('refreshBadges', handleBadgeRefresh);
    return () => window.removeEventListener('refreshBadges', handleBadgeRefresh);
  }, [fetchAllUnreadCounts]);

  // Listen for WebSocket updates
  useEffect(() => {
    if (chatUnreadCount !== undefined) {
      setMessageUnreadCount(chatUnreadCount);
    }
  }, [chatUnreadCount]);

  // Listen for WebSocket badge updates
  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.type === 'support_badge_update') {
        // Refetch support count
        api.get('/support/unread-count').then(res => {
          setSupportUnreadCount(res.data.count || 0);
        }).catch(() => {});
      } else if (lastMessage.type === 'notification_badge_update' || lastMessage.type === 'unread_count') {
        // Refetch notification count
        if (lastMessage.count !== undefined) {
          setNotificationUnreadCount(lastMessage.count);
        } else {
          api.get('/notifications/unread-count').then(res => {
            setNotificationUnreadCount(res.data.count || 0);
          }).catch(() => {});
        }
      } else if (lastMessage.type === 'new_message') {
        // Refetch message count
        api.get('/messages/unread-count').then(res => {
          setMessageUnreadCount(res.data.unread_count || 0);
        }).catch(() => {});
      }
    }
  }, [lastMessage]);

  // Close drawer on route change (mobile) — pathname only; avoid re-closing on setter identity
  useEffect(() => {
    mobileMenu.closeMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only close when the route changes
  }, [location.pathname]);

  // Prevent background scroll while mobile drawer is open
  useEffect(() => {
    if (!isMobileOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobileOpen]);

  const handleLogout = () => {
    setIsMobileOpen?.(false);
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    const items = {
      super_admin: [
        { icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
        { icon: Building2, label: 'Centres', path: '/admin/centers' },
        { icon: UserCircle, label: 'Admin CFA', path: '/admin/cfa-admins' },
        { icon: Ticket, label: 'Support', path: '/admin/platform-support', badge: supportUnreadCount },
        { icon: Bug, label: 'Rapports de bugs', path: '/admin/bug-reports' },
        { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
        { icon: Award, label: 'Abonnements', path: '/admin/subscriptions' },
        { icon: Bell, label: 'Notifications', path: '/notifications', badge: notificationUnreadCount },
        { icon: Settings, label: 'Paramètres', path: '/admin/platform-settings' },
      ],
      cfa_admin: [
        { icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
        { icon: Users, label: 'Étudiants', path: '/students' },
        { icon: GraduationCap, label: 'Formateurs', path: '/trainers' },
        { icon: Edit, label: 'Gestion des Cours', path: '/admin/courses' },
        { icon: BookOpen, label: 'Cours', path: '/courses' },
        { icon: FolderOpen, label: 'Bibliothèque', path: '/admin/resources' },
        { icon: Users, label: 'Classes / Groupes', path: '/admin/groups' },
        { icon: ClipboardCheck, label: 'Évaluations', path: '/trainer/evaluations' },
        { icon: BarChart3, label: 'Bilan Pédagogique', path: '/admin/pedagogical-report' },
        { icon: Euro, label: 'Finances', path: '/admin/finance' },
        { icon: Calendar, label: 'Planning', path: '/admin/planning' },
        { icon: ClipboardCheck, label: 'Émargement', path: '/admin/attendance' },
        { icon: Video, label: 'Sessions Live', path: '/admin/live-sessions' },
        { icon: Briefcase, label: 'Entreprises', path: '/admin/companies' },
        { icon: TrendingUp, label: 'Suivi Alternants', path: '/admin/alternants' },
        { icon: Megaphone, label: 'Annonces', path: '/admin/announcements' },
        { icon: FileText, label: 'Documents', path: '/documents' },
        { icon: MessageSquare, label: 'Messages', path: '/messages', badge: messageUnreadCount },
        { icon: Bell, label: 'Notifications', path: '/notifications', badge: notificationUnreadCount },
        { icon: Ticket, label: 'Support', path: '/support', badge: supportUnreadCount },
        { icon: Palette, label: 'Branding', path: '/admin/branding' },
        { icon: Settings, label: 'Paramètres', path: '/admin/settings' },
      ],
      trainer: [
        { icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
        { icon: Edit, label: 'Gestion des Cours', path: '/admin/courses' },
        { icon: BookOpen, label: 'Mes cours', path: '/courses' },
        { icon: FolderOpen, label: 'Bibliothèque', path: '/admin/resources' },
        { icon: Calendar, label: 'Mon planning', path: '/planning' },
        { icon: Users, label: 'Mes étudiants', path: '/students' },
        { icon: ClipboardCheck, label: 'Évaluations', path: '/trainer/evaluations' },
        { icon: ClipboardCheck, label: 'Émargement', path: '/trainer/attendance' },
        { icon: MessageSquare, label: 'Messages', path: '/messages', badge: messageUnreadCount },
        { icon: Bell, label: 'Notifications', path: '/notifications', badge: notificationUnreadCount },
        { icon: Ticket, label: 'Support', path: '/support', badge: supportUnreadCount },
      ],
      student: [
        { icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
        { icon: BookOpen, label: 'Mes cours', path: '/courses' },
        { icon: Calendar, label: 'Mon planning', path: '/student/planning' },
        { icon: Calendar, label: 'Lives & Replays', path: '/live-classes' },
        { icon: ClipboardCheck, label: 'Émargement', path: '/student/attendance' },
        { icon: FileText, label: 'Mes devoirs', path: '/homework' },
        { icon: BarChart3, label: 'Évaluations', path: '/evaluations' },
        { icon: BarChart3, label: 'Résultats', path: '/results' },
        { icon: MessageSquare, label: 'Forum', path: '/forum' },
        { icon: MessageSquare, label: 'Messages', path: '/messages', badge: messageUnreadCount },
        { icon: FileText, label: 'Mes documents', path: '/documents' },
        { icon: Bell, label: 'Notifications', path: '/notifications', badge: notificationUnreadCount },
        { icon: Ticket, label: 'Support', path: '/support', badge: supportUnreadCount },
      ],
    };

    return items[user?.role] || [];
  };

  const navItems = getNavItems();

  const isActive = (path) => location.pathname === path;

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const sidebarContent = (
    <div className="flex flex-col h-full min-h-0 bg-indigo-950 text-white overflow-hidden">
      {/* OFPilot Logo - Platform branding */}
      <div className="flex-shrink-0 p-4 border-b border-white/10 flex items-center justify-between gap-2">
        <Link
          to="/dashboard"
          className="inline-flex items-center"
          onClick={() => setIsMobileOpen?.(false)}
        >
          <span className="inline-flex items-center rounded-xl bg-white px-3 py-2 shadow-sm">
            <img 
              src="/images/ofpilot-logo-login.png" 
              alt="OFPilot" 
              className="h-7 w-auto object-contain"
              data-testid="ofpilot-logo"
            />
          </span>
        </Link>
        <button
          type="button"
          className="md:hidden p-2 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white"
          onClick={() => setIsMobileOpen?.(false)}
          aria-label="Fermer le menu"
          data-testid="mobile-menu-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Organization Header - if not super_admin */}
      {user?.role !== 'super_admin' && branding.organizationName && (
        <div className="flex-shrink-0 px-4 py-3 border-b border-white/10 bg-white/5">
          <div className="flex items-center gap-3">
            {branding.logoUrl ? (
              <img 
                src={branding.logoUrl.startsWith('http') ? branding.logoUrl : `${API_URL}${branding.logoUrl}`}
                alt={branding.organizationName}
                className="h-8 w-8 object-contain rounded"
                data-testid="org-logo"
              />
            ) : (
              <div 
                className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: branding.primaryColor }}
              >
                {branding.organizationName?.charAt(0) || 'O'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate" data-testid="org-name">
                {branding.organizationName}
              </p>
              <p className="text-xs text-slate-400">Centre de formation</p>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto sidebar-scroll">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setIsMobileOpen?.(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                active
                  ? 'bg-violet-600 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="flex-shrink-0 p-4 border-t border-white/10 space-y-2">
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle-btn"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-all duration-200 w-full"
        >
          {theme === 'light' ? (
            <Moon className="w-5 h-5" strokeWidth={1.5} />
          ) : (
            <Sun className="w-5 h-5" strokeWidth={1.5} />
          )}
          <span className="font-medium">Thème {theme === 'light' ? 'sombre' : 'clair'}</span>
        </button>
        
        <Link
          to={user?.role === 'trainer' ? '/trainer/profile' : '/profile'}
          data-testid="nav-profile"
          onClick={() => setIsMobileOpen?.(false)}
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-all duration-200"
        >
          <UserCircle className="w-5 h-5" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </Link>
        
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition-all duration-200 w-full"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span className="font-medium">Déconnexion</span>
        </button>
      </div>
      
      {/* Powered by OFPilot footer */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 bg-white/5 text-center">
        <p className="text-xs text-slate-400">
          Powered by <span className="font-semibold text-white">OFPilot</span>
        </p>
      </div>
    </div>
  );

  // Portal to document.body so Android Chrome doesn't clip/trap position:fixed
  // inside ancestors with overflow-x-hidden (common cause of "menu does nothing").
  const drawer = (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[85] md:hidden"
          onClick={() => mobileMenu.closeMenu()}
          aria-hidden="true"
          data-testid="mobile-menu-overlay"
        />
      )}

      {/*
        pointer-events-none while closed: during the slide-out animation the
        aside would otherwise still intercept taps on the ☰ button.
        z-index sits above the sticky Header (z-70) so the open drawer is never
        hidden behind it on mobile.
      */}
      <aside
        className={`fixed top-0 left-0 h-[100dvh] w-64 z-[90] transition-transform duration-300 ease-in-out ${
          isMobileOpen
            ? 'translate-x-0 pointer-events-auto'
            : '-translate-x-full pointer-events-none'
        } md:translate-x-0 md:pointer-events-auto`}
        data-testid="app-sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(drawer, document.body);
};

export default Sidebar;
